/** @jsx jsx */
import { css, jsx } from "@emotion/core"

import { ConditionalWrapper, Icon, List } from "@cd2/components"
import { format } from "date-fns"
import { Link } from "react-router-dom"
import { themeColors } from "../Components/EOSThemeProvider"
import { useGlobalState } from "../Components/GlobalStateProvider"
import { LabelInfo } from "../Components/LabelInfo"
import { QuickToolTip } from "../Components/QuickTooltip"
import { icons } from "../constants/icons"
import { buildRoute, eRoutes } from "../routes"
import { IRecord } from "../types/schema"
import { getTotals } from "../utils/getQuoteOrderTotals"
import { formatPrice } from "../utils/utils"
import { HoverCustomerLoader } from "./HoverCustomerLoader"
import { HoverResourceLoader } from "./HoverResourceLoader"
import { HoverUserLoader } from "./HoverUserLoader"

type IQuote = IRecord<
  "orders",
  "identifier" | "carriage_pennies" | "updated_at" | "delivery" | "sage_order_number" | "created_at"
> & {
  branch: IRecord<"branches", "name"> | null
  customer: IRecord<
    "customers",
    "name" | "account_reference" | "cash_sales" | "default_sage_tax_rate_id" | "tax_rate"
  >
  items: IRecord<
    "quote_items",
    "type" | "price_pennies" | "quantity" | "body" | "stock_code" | "discountPercent"
  >[]
  user: IRecord<"skeem_users", "skeem_email" | "name">
}

interface IProps {
  id: string
  initialData?: IQuote
  renderInlineLink?: "block" | "inline"
  inline?: boolean
}

export function HoverOrderLoader({ id, initialData, renderInlineLink, inline }: IProps) {
  const skeem = window.skeem
  const { currency } = useGlobalState()

  async function loadData() {
    const quote = await skeem.fetchOne<IQuote>({
      orders: {
        attributes: [
          "identifier",
          "carriage_pennies",
          "updated_at",
          "created_at",
          "delivery",
          "sage_order_number",
          { name: "branch", attributes: ["name"] },
          {
            name: "customer",
            attributes: [
              "name",
              "account_reference",
              "cash_sales",
              "default_sage_tax_rate_id",
              "tax_rate",
            ],
          },
          { name: "user", attributes: ["skeem_email", "name"] },
          {
            name: "items",
            attributes: [
              "price_pennies",
              "markUp",
              "cost_price_pennies",
              "quantity",
              "discountPercent",
            ],
          },
        ],
        filter: { matches: { id } },
      },
    })
    return quote
  }

  return (
    <HoverResourceLoader
      reloadKey={id}
      loadData={loadData}
      initialData={initialData}
      inline={inline}
      viewLink={order => buildRoute(eRoutes["/orders/:id/preview"], { id: order.id })}
      renderInlineLink={renderInlineLink}
      resourceName="Order"
      renderHoverContentTitle={record => record.identifier}
      renderHoverContent={record => (
        <div>
          <List>
            <LabelInfo labelWidth={80} label="Identifier" value={record.identifier} inline />
            <LabelInfo labelWidth={80} label="Branch" value={record.branch?.name} inline />
            <LabelInfo
              labelWidth={80}
              label="Customer"
              value={
                <HoverCustomerLoader
                  id={record.customer?.id}
                  initialData={record.customer as any}
                />
              }
              inline
            />
            <LabelInfo
              labelWidth={80}
              label="Total Price"
              value={formatPrice(
                getTotals(record.items, record.carriage_pennies, record.customer?.tax_rate)
                  .totalPricePennies,
                { isPennies: true, currency },
              )}
              inline
            />
            <LabelInfo
              labelWidth={80}
              label="Taken By"
              value={
                record.user ? (
                  <HoverUserLoader id={record.user.id} initialData={record.user} />
                ) : null
              }
              inline
            />
            <LabelInfo
              labelWidth={80}
              label="Delivery"
              value={
                record.delivery ? (
                  <Link
                    to={buildRoute(eRoutes["/deliveries/:id/preview"], { id: record.delivery.id })}
                  >
                    View delivery
                  </Link>
                ) : (
                  "No Delivered"
                )
              }
              inline
            />
            <LabelInfo
              labelWidth={80}
              label="Sage no"
              value={record.sage_order_number ? record.sage_order_number : "Not Exported to Sage"}
              inline
            />
            <LabelInfo
              labelWidth={80}
              label="Created At"
              value={record.created_at ? format(new Date(record.created_at), "dd/MM/yy p") : "-"}
              inline
            />
          </List>
        </div>
      )}
      renderTarget={record => (
        <span>
          {record.identifier}
          <ConditionalWrapper
            condition={!!record.delivery || !!record.sage_order_number}
            Wrapper={QuickToolTip}
            wrapperProps={{
              content:
                record.delivery && !record.sage_order_number
                  ? "Order has been marked for delivery"
                  : record.sage_order_number && !record.delivery
                  ? "Order has been exported to Sage"
                  : "Order marked for delivery and exported to Sage",
            }}
          >
            {record.delivery && (
              <span
                css={css`
                  display: inline-block;
                  margin-left: 2px;
                `}
              >
                <Icon icon={icons.check} size={10} color={themeColors.green} />
              </span>
            )}
            {record.sage_order_number && (
              <span
                css={css`
                  display: inline-block;
                  margin-left: -6px;
                `}
              >
                <Icon icon={icons.check} size={10} color={themeColors.green} />
              </span>
            )}
          </ConditionalWrapper>
        </span>
      )}
    />
  )
}
